import { useReactiveVar } from '@apollo/client'
import { Box, Button, Slide, styled, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import theme from '../../../../config/theme'
import { modalVar } from '../../../../providers/apollo/cache'
import Crown from '../../../../svg/Crown'
import useBucket from '../../hooks/useBucket'

// Styled components for the toast
const ToastContainer = styled(Box)(() => ({
  borderRadius: '16px',
  overflow: 'hidden',
  maxWidth: '450px',
  width: 'fit-content',
  margin: '0 auto 16px auto',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
  position: 'fixed',
  bottom: '0',
  left: '16px',
  right: '16px',
  zIndex: 1300
}))

const ModalContent = styled(Box)({
  background: '#FFFFFF',
  padding: '20px 20px',
  textAlign: 'center',
  position: 'relative'
})

const TitleText = styled(Typography)({
  fontWeight: 'bold',
  fontSize: '16px',
  color: '#000000',
  marginBottom: '6px',
  lineHeight: '1.4'
})

const SubtitleText = styled(Typography)({
  fontWeight: 'normal',
  fontSize: '14px',
  color: '#000000',
  marginBottom: '16px',
  lineHeight: '1.4'
})

const UpgradeButton = styled(Button)({
  borderRadius: '25px',
  padding: '1px 24px',
  fontSize: '16px',
  fontWeight: 'bold',
  textTransform: 'none',
  width: '100%',
  background: theme.palette.groupsy.gradient,
  color: '#FFFFFF',
  boxShadow: '0 4px 15px rgba(155, 89, 182, 0.3)',
  '&:hover': {
    boxShadow: '0 6px 20px rgba(155, 89, 182, 0.4)'
  },
  '&:active': {
    transform: 'translateY(1px)'
  }
})

const ButtonContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
})

export default function SimpleUpsellToast() {
  const modal = useReactiveVar(modalVar)
  const { currentUserIsOwner } = useBucket()
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (modal === 'simpleUpsellToast') {
      setIsVisible(true)
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setIsVisible(false)
        modalVar(null)
      }, 3000)

      return () => clearTimeout(timer)
    }
    return undefined
  }, [modal])

  const handleUpgrade = () => {
    // Handle upgrade logic here
    setIsVisible(false)
    modalVar(currentUserIsOwner ? 'upsellModal' : 'premiumFeature')
  }

  if (!isVisible) return null

  return (
    <Slide direction="up" in={isVisible} mountOnEnter unmountOnExit>
      <ToastContainer>
        <ModalContent>
          <TitleText>On the free plan, only your most recent 50 images are visible.</TitleText>
          <SubtitleText>Discover your full gallery by upgrading today!</SubtitleText>
          <UpgradeButton variant="contained" onClick={handleUpgrade}>
            <ButtonContent>
              <Crown size={24} sx={{ marginRight: 1, marginTop: '0.5em' }} />
              <Typography variant="pb.label.small" sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                Premium
              </Typography>
            </ButtonContent>
          </UpgradeButton>
        </ModalContent>
      </ToastContainer>
    </Slide>
  )
}

// Helper function to open the modal programmatically
export const openSimpleUpsellToast = () => {
  modalVar('simpleUpsellToast')
}
